import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';

import { CHART_COLORS, chartTooltipStyle } from '@/features/reports/lib/chartTheme';
import { formatNumber, formatPercent, formatToman } from '@/features/reports/lib/format';

/**
 * Donut for a share-of-total breakdown (payment methods, collections).
 *
 * The centre shows the total so the ring never needs a separate legend figure.
 */
export default function DonutChart({
    rows = [],
    labelKey = 'label',
    valueKey = 'amount',
    height = 300,
    centerLabel = 'جمع کل',
    money = true,
}) {
    // Recharts only accumulates numeric data points, and the report API
    // serialises money as a decimal string, so the ring would come out empty.
    // Normalise to numbers before the data reaches the chart.
    const data = rows.map((row) => ({ ...row, [valueKey]: Number(row[valueKey] ?? 0) }));
    const total = data.reduce((sum, row) => sum + row[valueKey], 0);
    const format = money ? formatToman : formatNumber;

    const tooltip = ({ active, payload }) => {
        if (!active || !payload?.length) return null;
        const row = payload[0].payload;
        return (
            <div className="report-tooltip">
                <p className="report-tooltip-title">{row[labelKey]}</p>
                <p className="report-tooltip-row">
                    <span>مبلغ</span>
                    <strong>{format(row[valueKey])}</strong>
                </p>
                <p className="report-tooltip-row report-tooltip-muted">
                    <span>سهم</span>
                    <span>{formatPercent(row.share_pct ?? 0)}</span>
                </p>
                {row.transactions !== undefined && (
                    <p className="report-tooltip-row report-tooltip-muted">
                        <span>تراکنش</span>
                        <span>{formatNumber(row.transactions)}</span>
                    </p>
                )}
            </div>
        );
    };

    return (
        <div style={{ position: 'relative', width: '100%', height }}>
            <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                    <Pie
                        data={data}
                        cx="50%"
                        cy="50%"
                        innerRadius="58%"
                        outerRadius="80%"
                        paddingAngle={3}
                        dataKey={valueKey}
                        nameKey={labelKey}
                    >
                        {data.map((row, index) => (
                            <Cell key={row.method ?? row.status ?? index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                    </Pie>
                    <Tooltip content={tooltip} contentStyle={chartTooltipStyle} />
                    <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ fontFamily: 'var(--font)', fontSize: 12 }} />
                </PieChart>
            </ResponsiveContainer>

            <div className="report-donut-center">
                <span className="report-donut-label">{centerLabel}</span>
                <strong className="report-donut-value">{format(total)}</strong>
            </div>
        </div>
    );
}
